import React, { useState } from 'react';
import { useDraggable } from '@dnd-kit/core';
import { FiChevronRight, FiCheckCircle, FiX } from 'react-icons/fi';
import GestaoOrderCard from '../../components/cards/GestaoOrderCard';

export function KanbanCard({ order, colId, onUpdate }) {
    const [fullCardOpen, setFullCardOpen] = useState(false);

    const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
        id: order._id,
        data: { order, colId }
    });

    const style = transform ? {
        transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
    } : undefined;

    const totalLines = order.itens?.reduce((acc, it) => acc + (Number(it.quantidade) || 0), 0) || 0;
    const totalMonthly = order.itens?.reduce((acc, it) => acc + ((Number(it.quantidade) || 0) * (Number(it.precoUnitario) || 0)), 0) || 0;
    
    // MOCK DDD (if missing from order)
    const ddd = order.empresa?.telefone ? order.empresa.telefone.substring(0, 2) : 'N/A';

    // Calculate SLA Status (mocked 24h logic)
    const createdAt = new Date(order.createdAt || Date.now());
    const updatedAt = new Date(order.updatedAt || Date.now());
    const hoursSinceUpdate = Math.abs(Date.now() - updatedAt) / 36e5;
    const isSlaOk = hoursSinceUpdate <= 24;

    return (
        <>
            <div 
                ref={setNodeRef} 
                {...listeners} 
                {...attributes} 
                style={style}
                className={`bg-[var(--bg-card)] border border-[var(--border-color)] p-5 rounded-[2rem] shadow-2xl cursor-grab active:cursor-grabbing mb-4 ${isDragging ? 'opacity-70 ring-2 ring-[var(--accent-color)] z-50' : ''}`}
            >
                {/* Protocolo */}
                <div className="flex items-center gap-2 mb-6 bg-[var(--bg-main)]/40 w-fit px-4 py-2 rounded-full border border-[var(--border-color)]">
                    <FiCheckCircle className="text-[var(--text-secondary)]" size={14} />
                    <span className="text-[var(--text-primary)] text-xs font-semibold">Protocolo Pedido:</span>
                    <span className="text-[var(--text-primary)] font-mono text-xs">{order.protocolo || 'NÃO GERADO'}</span>
                </div>

                {/* Info Block */}
                <div className="bg-[var(--bg-main)]/20 p-4 rounded-3xl border border-[var(--border-color)] mb-6 space-y-2 pl-6">
                    <div className="text-xs text-[var(--text-primary)]"><span className="opacity-70">DDD:</span> <span className="font-semibold">{ddd}</span></div>
                    <div className="text-xs text-[var(--text-primary)]"><span className="opacity-70">Nome Empresa:</span> <span className="font-semibold">{order.empresa?.razaoSocial || order.clienteNome || '---'}</span></div>
                    <div className="text-xs text-[var(--text-primary)]"><span className="opacity-70">Quantidade Linhas:</span> <span className="font-semibold">{totalLines}</span></div>
                    <div className="text-xs text-[var(--text-primary)]"><span className="opacity-70">Valor do Contrato:</span> <span className="font-semibold">{totalMonthly.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</span></div>
                </div>

                {/* Glowing Separator */}
                <div className="w-1/2 mx-auto h-[2px] bg-gradient-to-r from-transparent via-indigo-500 to-transparent mb-6 opacity-50 shadow-[0_0_10px_rgba(99,102,241,0.8)]"></div>

                {/* Dates & SLA */}
                <div className="bg-[var(--bg-main)] p-4 rounded-2xl border border-[var(--border-color)] mb-6 flex justify-between items-center group cursor-pointer hover:border-[var(--accent-color)] transition-colors">
                    <div className="space-y-1">
                        <div className="text-[10px] text-[var(--text-primary)]"><span className="opacity-70">Data entrada:</span> {createdAt.toLocaleDateString('pt-BR')}</div>
                        {order.dataConclusao ? (
                            <div className={`text-[10px] font-bold ${colId === 'col-cancelado' ? 'text-red-500' : 'text-green-500'}`}>
                                <span className="opacity-70 text-[var(--text-primary)] font-normal">
                                    {colId === 'col-cancelado' ? 'Data Cancelamento:' : 'Data Faturamento:'}
                                </span> {new Date(order.dataConclusao).toLocaleDateString('pt-BR')}
                            </div>
                        ) : (
                            <div className="text-[10px] text-[var(--text-primary)]"><span className="opacity-70">Data atualização:</span> {updatedAt.toLocaleDateString('pt-BR')}</div>
                        )}
                        <div className="text-[10px] text-[var(--text-primary)] flex items-center gap-2 mt-2 pt-2 border-t border-[var(--border-color)]">
                            <span className="opacity-70 flex items-center gap-1">
                                <svg stroke="currentColor" fill="none" strokeWidth="2" viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round" height="12" width="12" xmlns="http://www.w3.org/2000/svg"><circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 16 14"></polyline></svg>
                                SLA 24h (Atualização):
                            </span> 
                            <span className={isSlaOk ? 'text-emerald-400 font-bold' : 'text-red-500 font-black animate-pulse flex items-center gap-1'}>
                                {!isSlaOk && <span>⚠️</span>} {isSlaOk ? 'NO PRAZO' : 'ATRASADO'}
                            </span>
                        </div>
                    </div>
                    <FiChevronRight className="text-indigo-400 group-hover:translate-x-1 transition-transform" />
                </div>

                {/* Bottom Buttons */}
                <div className="flex gap-2">
                    <div className="flex-1 bg-black/40 border border-[#1a1a3a] rounded-xl p-3 flex flex-col justify-center items-center">
                        <span className="text-[9px] text-gray-400 mb-1">Status atual:</span>
                        <span className="text-[10px] font-bold text-white text-center truncate w-full">{order.status || 'Pendente'}</span>
                    </div>

                    {(colId === 'col-concluido' || colId === 'col-cancelado') && (
                        <button 
                            onPointerDown={(e) => e.stopPropagation()} 
                            onClick={(e) => { 
                                e.stopPropagation(); 
                                const msg = 'Digite a data atualizada (AAAA-MM-DD):';
                                const newDate = window.prompt(msg, new Date().toISOString().split('T')[0]);
                                if (newDate) {
                                    try {
                                        const isoDate = new Date(newDate + 'T12:00:00Z').toISOString();
                                        const subCat = colId === 'col-cancelado' ? 'ABORTADO' : (order.subCategoriaInterna || 'FINALIZADO');
                                        if (onUpdate) onUpdate(order._id, order.status, 'Alteração manual de data atualizada', order.categoriaInterna || 'LINHAS_MOVEIS', subCat, isoDate);
                                    } catch(e) {
                                        alert("Data inválida. Use o formato AAAA-MM-DD");
                                    }
                                }
                            }}
                            className={`rounded-xl p-2 flex items-center justify-center transition-all group shrink-0 w-20 ${colId === 'col-cancelado' ? 'bg-red-900/30 hover:bg-red-600 border border-red-800 hover:border-red-400' : 'bg-green-900/30 hover:bg-green-600 border border-green-800 hover:border-green-400'}`}
                            title="Ajustar Data Atualizada"
                        >
                            <span className={`text-[8px] font-bold group-hover:text-white transition-all text-center leading-tight ${colId === 'col-cancelado' ? 'text-red-300' : 'text-green-300'}`}>
                                Data Atualizada
                            </span>
                        </button>
                    )}

                    <button 
                        onPointerDown={(e) => e.stopPropagation()} 
                        onClick={(e) => { e.stopPropagation(); setFullCardOpen(true); }}
                        className="flex-1 bg-[#0a0a20] hover:bg-indigo-600 border border-[#1a1a3a] hover:border-indigo-500 rounded-xl p-3 flex items-center justify-center transition-all group"
                    >
                        <span className="text-[10px] font-bold text-white group-hover:tracking-wider transition-all">Abrir pedido</span>
                    </button>
                </div>
            </div>

            {/* MODAL DO CARD COMPLETO (VIDA DO PEDIDO) */}
            {fullCardOpen && (
                <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/80 backdrop-blur-md px-4 overflow-y-auto py-10" onClick={(e) => { e.stopPropagation(); setFullCardOpen(false); }}>
                    <div className="w-full max-w-2xl bg-black rounded-[2rem] border border-[var(--accent-color)] relative" onClick={e => e.stopPropagation()}>
                        <button onClick={() => setFullCardOpen(false)} className="absolute top-4 right-4 z-50 p-2 bg-black/50 text-white rounded-full hover:bg-red-500 transition-all border border-white/10">
                            <FiX size={20} />
                        </button>
                        <GestaoOrderCard order={order} onStatusUpdate={(id, status, comment, cat, sub, date) => {
                            setFullCardOpen(false);
                            if (onUpdate) onUpdate(id, status, comment, cat, sub, date);
                        }} />
                    </div>
                </div>
            )}
        </>
    );
}
